import { useState } from "react";
import { ArrowLeft, HelpCircle, ChevronDown, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const faqData = [
  {
    category: "About our pilot",
    items: [
      {
        q: "What does it mean that Sarathi-X is in pilot phase?",
        a: "Sarathi-X is currently in its pilot phase, running from June to August 2026 with a single car and a single driver — our co-founder, Vishal. This means availability is limited. We receive more requests than we can always accommodate, and while we will do everything possible to confirm your ride, we may occasionally have to decline a booking if we are already committed. If your booking is declined, we will always let you know immediately and suggest the next available slot. We will not leave you without a response. We have chosen to start small deliberately — one car, one driver, every ride personally handled — because we would rather do a few things exceptionally well than many things adequately. Every booking during this pilot helps us understand what Sarathi-X needs to be before we grow. If you book with us during this period, thank you. We are grateful. You are part of what we are building.",
      },
    ],
  },
  {
    category: "Booking",
    items: [
      { q: "How do I book a ride with Sarathi-X?", a: "You can book through our website or app. Enter your pickup location, drop location, date, and time to see your fare instantly. Submit your details and we will confirm your booking on WhatsApp within 30 minutes." },
      { q: "Is my booking confirmed immediately?", a: "Once you submit a booking request, our team reviews availability and confirms via WhatsApp. You will receive confirmation within 30 minutes. Until you receive a WhatsApp confirmation, your booking is a request — not a confirmed ride." },
      { q: "How far in advance should I book?", a: "We recommend booking at least 3 hours in advance. For late night, midnight, or early morning flights, please book at least 5 hours in advance." },
      { q: "Can I book for someone else?", a: "Yes. Enter the passenger's name and phone number in the booking form. We will coordinate directly with them for pickup." },
      { q: "Do I need to create an account?", a: "No. You can book as a guest with just your name and phone number." },
    ],
  },
  {
    category: "Fares and Pricing",
    items: [
      { q: "Is the fare fixed or will it change?", a: "The fare shown at booking is fixed. There is no surge pricing, no hidden charges, and no fare changes after booking. What you see is what you pay." },
      { q: "What is included in the fare?", a: "In-city airport transfers: All-inclusive. Base charge, tolls, and GST are bundled into one fixed fare. Nothing added at the end of the ride. Outstation transfers: Same — base charge, tolls, and GST are all included. The only addition is a driver allowance of \u20B9150, which covers the driver's time on longer routes. This is shown upfront at booking." },
      { q: "Are there any cancellation charges?", a: "Cancellations more than 3 hours before pickup are free. Cancellations between 1\u20132 hours of pickup may attract a charge of up to 50% of the fare. Last-minute cancellation and no-shows will be charged the full fare." },
      { q: "What payment methods do you accept?", a: "UPI and cash are accepted directly at the end of your ride. If you prefer paying by card or online, we will send a secure Razorpay payment link to your WhatsApp the moment your ride is complete." },
    ],
  },
  {
    category: "Airport Transfers",
    items: [
      { q: "Which airports do you serve?", a: "We currently serve Kempegowda International Airport (KIAL), Bengaluru — Terminal 1 and Terminal 2." },
      { q: "What happens if my flight is delayed?", a: "If you provide your flight number at booking, we track your flight in real time. Your driver will wait up to 30 minutes at no extra charge. For longer delays, we charge a fee of \u20B9100 per hour." },
      { q: "Where does the driver meet me at the airport?", a: "Your driver will meet you at the P4 exit. Driver details including name, phone number, and vehicle registration will be shared with you at the time of booking confirmation." },
      { q: "Do you offer meet and greet service?", a: "Yes, for a fee of \u20B9100, your driver waits at the arrivals exit with a nameplate." },
    ],
  },
  {
    category: "Vehicle and Safety",
    items: [
      { q: "What vehicle will I be travelling in?", a: "All Sarathi-X rides are in the MG Windsor EV — a premium electric vehicle with a silent cabin, spacious seating for up to 4 passengers, and a large 605-litre boot." },
      { q: "How many passengers can travel?", a: "The MG Windsor EV seats up to 4 passengers comfortably with standard airport luggage." },
      { q: "Is the vehicle safe?", a: "Yes. The vehicle is equipped with 6 airbags, an AI-powered dual dashcam system (front and interior and back), and an advanced SOS system that alerts our command centre in the event of an emergency, or in case any assistance is needed." },
    ],
  },
  {
    category: "Cancellation and Changes",
    items: [
      { q: "How do I cancel a booking?", a: "If you need to cancel the ride, just click on the \u201CCancel Ride\u201D button on the website or the app. Cancellations more than 3 hours before pickup are free. Cancellations between 1\u20132 hours of pickup may attract a charge of up to 50% of the fare. Last-minute cancellation will be charged the full fare." },
      { q: "Can I change my pickup time or location after booking?", a: "Yes, subject to availability. Contact us via WhatsApp as soon as possible. Changes close to the pickup time may not always be possible." },
      { q: "What if Sarathi-X needs to cancel my booking?", a: "In the rare event we need to cancel a confirmed booking due to a vehicle or driver emergency, we will notify you immediately and process a full refund if payment has been made. We will also assist you in finding an alternative. Our team will never leave your side and ensure your safety and comfort throughout your journey." },
    ],
  },
  {
    category: "General",
    items: [
      { q: "Do you operate 24 hours?", a: "Yes. We accept bookings at any hour, including early morning and late-night pickups. Same flat fare 24×7 — no surge, no time-of-day surcharge." },
      { q: "What areas do you serve?", a: "We serve all areas within Bengaluru city and outstation destinations including Bidadi, Ramanagara, Mysuru, Hosur, Nandi Hills, and other locations. Enter your pickup address on the booking page to see your fare." },
      { q: "What if I leave something in the vehicle?", a: "Contact us immediately via WhatsApp with your booking reference. We will make every effort to return lost items. Sarathi-X is not liable for items left in the vehicle but we will do our best to help." },
      { q: "How do I give feedback about my ride?", a: "WhatsApp us after your ride or email founder@sarathix.in. All feedback is reviewed by the founding team personally." },
      { q: "Is Sarathi-X available outside Bengaluru?", a: "Currently we operate only in Bengaluru and serve outstation destinations from Bengaluru as the origin. We have plans to expand — follow us for updates." },
    ],
  },
];

const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/5 last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-start justify-between gap-3 py-4 text-left"
        data-testid={`faq-q-${q.slice(0, 20).replace(/\s/g, '-').toLowerCase()}`}
      >
        <span className="text-white text-sm font-medium">{q}</span>
        <ChevronDown className={`w-4 h-4 text-sarathi-text-muted shrink-0 mt-0.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <p className="text-sarathi-text-secondary text-sm leading-relaxed pb-4 whitespace-pre-line">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function FAQPage() {
  return (
    <div className="min-h-screen bg-sarathi-background text-white">
      <div className="border-b border-white/10 bg-sarathi-surface/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <Link to="/" className="w-9 h-9 rounded-full bg-sarathi-background border border-white/10 flex items-center justify-center text-sarathi-text-muted hover:text-white transition-colors" data-testid="faq-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div className="flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-sarathi-primary" />
            <h1 className="font-outfit text-lg font-semibold">FAQs</h1>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-10 space-y-8" data-testid="faq-content">
        <div>
          <h2 className="font-outfit text-2xl font-bold">Frequently Asked Questions</h2>
          <p className="text-sarathi-text-muted text-sm mt-2">Everything you need to know about Sarathi-X.</p>
        </div>

        {faqData.map((section) => {
          const isPilot = section.category === "About our pilot";
          return (
            <div key={section.category}>
              <h3 className={`font-outfit text-base font-semibold uppercase tracking-wide mb-3 flex items-center gap-2 ${isPilot ? "text-sarathi-primary" : "text-sarathi-primary"}`}>
                {isPilot && <Sparkles className="w-4 h-4" />}
                {section.category}
              </h3>
              <div className={`bg-sarathi-surface border rounded-xl px-5 ${isPilot ? "border-sarathi-primary/30" : "border-white/10"}`}>
                {section.items.map((item) => (
                  <FaqItem key={item.q} q={item.q} a={item.a} />
                ))}
              </div>
            </div>
          );
        })}

        <div className="border-t border-white/10 pt-6 text-center">
          <p className="text-sarathi-text-muted text-sm mb-3">Still have questions?</p>
          <a
            href="https://wa.me/919187057303?text=Hi%2C%20I%20have%20a%20question%20about%20Sarathi-X."
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#25D366]/10 border border-[#25D366]/30 rounded-full px-5 py-2.5 text-[#25D366] text-sm font-medium hover:bg-[#25D366]/20 transition-colors"
          >
            Chat with us on WhatsApp
          </a>
          <div className="mt-4">
            <Link to="/" className="text-sarathi-primary hover:underline text-sm font-medium" data-testid="faq-home-link">Back to Sarathi-X Home</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
